import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import * as signalR from "@microsoft/signalr";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const STATUSES = {
  pending: "⏳ Baxılır",
  approved: "✅ Təsdiqləndi",
  rejected: "❌ Rədd edildi"
};

const PAGE_SIZE = 5;

const AllComplaintsDashboard = () => {
  const [complaintsCache, setComplaintsCache] = useState({
    pending: { data: [], page: 1, hasMore: true },
    approved: { data: [], page: 1, hasMore: true },
    rejected: { data: [], page: 1, hasMore: true }
  });

  const [statusFilter, setStatusFilter] = useState("pending");
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [replies, setReplies] = useState({});
  const [processing, setProcessing] = useState({});

  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    if (complaintsCache[statusFilter].data.length === 0) {
      fetchComplaints(statusFilter, 1);
    }
  }, [statusFilter]);

  useEffect(() => {
    const connection = new signalR.HubConnectionBuilder()
      .withUrl("https://api.herracim.az/hubs/complaints", { withCredentials: true })
      .withAutomaticReconnect()
      .build();

    connection.start().then(() => {
      console.log("✅ SignalR connected to complaints hub");

      connection.on("ComplaintUpdated", () => {
        setComplaintsCache(prev => ({
          ...prev,
          [statusFilter]: { data: [], page: 1, hasMore: true }
        }));
        fetchComplaints(statusFilter, 1, true);
      });
    });

    return () => {
      connection.stop();
    };
  }, [statusFilter]);

  const fetchComplaints = async (status, page, replace = false) => {
    try {
      setLoading(true);

      const params = new URLSearchParams({
        status,
        page: page.toString(),
        pageSize: PAGE_SIZE.toString(),
        search: searchTerm,
        startDate,
        endDate
      });

      const res = await axios.get(
        `https://api.herracim.az/api/dashboard/complaints?${params.toString()}`,
        { withCredentials: true }
      );

      const newData = res.data;
      setComplaintsCache(prev => ({
        ...prev,
        [status]: {
          data: replace ? newData : [...prev[status].data, ...newData],
          page: page + 1,
          hasMore: newData.length === PAGE_SIZE
        }
      }));
    } catch {
      toast.error("❌ Şikayətlər yüklənə bilmədi.");
    } finally {
      setLoading(false);
    }
  };

  const handleDecision = async (id, approve) => {
    const reply = replies[id];
    if (!reply || !reply.trim()) {
      toast.warning("📭 Cavab boş ola bilməz.");
      return;
    }

    if (!window.confirm(approve ? "Şikayəti TƏSDİQLƏYİRSİNİZ?" : "Şikayəti RƏDD EDİRSİNİZ?")) return;

    setProcessing(prev => ({ ...prev, [id]: true }));

    try {
      await axios.post(`https://api.herracim.az/api/dashboard/complaint-decision/${id}`, { approve,  adminReply: replies[id] }, { withCredentials: true });
      await axios.post(`https://api.herracim.az/api/dashboard/complaint-reply/${id}`, { reply }, { withCredentials: true });
      toast.success("✅ Qərar və cavab göndərildi");

      // SignalR xəbərdarlığı gələcək, amma fallback üçün manual fetch
      setComplaintsCache(prev => ({
        ...prev,
        [statusFilter]: { data: [], page: 1, hasMore: true }
      }));
      fetchComplaints(statusFilter, 1, true);
    } catch {
      toast.error("❌ Əməliyyat alınmadı");
    } finally {
      setProcessing(prev => ({ ...prev, [id]: false }));
    }
  };

  const calculateDelay = (depositConfirmedAt, createdAt) => {
    if (!depositConfirmedAt || !createdAt) return "-";
    const diffMs = new Date(createdAt) - new Date(depositConfirmedAt);
    const diffMinutes = Math.floor(diffMs / 60000);
    if (diffMinutes < 1) return "⏳ Az sonra şikayət edilib";
    if (diffMinutes < 60) return `${diffMinutes} dəqiqə sonra`;
    const h = Math.floor(diffMinutes / 60), m = diffMinutes % 60;
    return `${h} saat${m ? ` ${m} dəqiqə` : ""} sonra`;
  };

  const currentData = complaintsCache[statusFilter].data;
  const currentPage = complaintsCache[statusFilter].page;
  const hasMore = complaintsCache[statusFilter].hasMore;

  const handleSearchFilter = () => {
    setComplaintsCache(prev => ({
      ...prev,
      [statusFilter]: { data: [], page: 1, hasMore: true }
    }));
    fetchComplaints(statusFilter, 1, true);
  };

  const renderComplaint = (c) => {
    const isExpanded = expandedId === c.id;
    return (
      <div key={c.id} className="complaint-item">
        <div className="complaint-summary" onClick={() => setExpandedId(isExpanded ? null : c.id)}>
          <strong>{c.productName}</strong> — {c.buyerFullName} → {STATUSES[c.status.toLowerCase()]}
        </div>

        {isExpanded && (
          <div className="complaint-details">
            <p><strong>📦 Məhsul:</strong> <Link to={`/product/${c.productId}`}>{c.productName}</Link></p>
            <p><strong>🏪 Satıcı:</strong> <Link to={`/user/${c.sellerUserId}`}>{c.sellerFullName}</Link></p>
            <p><strong>🧍 Alıcı:</strong> <Link to={`/user/${c.buyerUserId}`}>{c.buyerFullName}</Link></p>
            <p><strong>📅 Şikayət tarixi:</strong> {new Date(c.createdAt).toLocaleString()}</p>
            <p><strong>📩 Məzmun:</strong> {c.reportMessage}</p>
            <p><strong>⏱ Gecikmə:</strong> {calculateDelay(c.depositConfirmedAt, c.createdAt)}</p>
            <p><strong>🤝 Görüş vaxtı:</strong> {c.productMetAt ? new Date(c.productMetAt).toLocaleString() : "-"}</p>

            {c.adminReply && (
              <div className="admin-reply-info">
                <strong>👨‍💼 Admin cavabı:</strong>
                <p>{c.adminReply}</p>
              </div>
            )}

            {c.status === "pending" && (
              <>
                <textarea
                  placeholder="Cavab yazın..."
                  value={replies[c.id] || ""}
                  onChange={e => setReplies(prev => ({ ...prev, [c.id]: e.target.value }))}
                />
                <div className="decision-buttons">
                  <button onClick={() => handleDecision(c.id, true)} disabled={processing[c.id]}>✔️ Təsdiqlə</button>
                  <button onClick={() => handleDecision(c.id, false)} disabled={processing[c.id]}>❌ Rədd et</button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="complaints-dashboard">
      <ToastContainer position="top-right" autoClose={3000} />

      <h2>🧾 Şikayətlər Paneli</h2>

      <div className="tabs">
        {Object.entries(STATUSES).map(([key, label]) => (
          <button key={key} onClick={() => setStatusFilter(key)} className={statusFilter === key ? "active" : ""}>
            {label}
          </button>
        ))}
      </div>

      <div className="filters">
        <input
          type="text"
          placeholder="🔍 Məhsul, alıcı, satıcı..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        <button onClick={handleSearchFilter}>Filterlə</button>
      </div>

      {currentData.map(renderComplaint)}

      {hasMore && (
        <button className="load-more" onClick={() => fetchComplaints(statusFilter, currentPage)}>Daha çox yüklə ⬇️</button>
      )}

      {loading && <p>⏳ Yüklənir...</p>}
    </div>
  );
};

export default AllComplaintsDashboard;
